import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import Sidebar from "../../components/Sidebar/Sidebar";

const API_BASE = "https://appapi.unpaidfinancial.com";
const SIDEBAR_W = 285;

export default function EnvoiEmail() {
  const history = useHistory();

  const [mode, setMode] = useState("SELECT"); // SELECT | ALL
  const [utilisateurs, setUtilisateurs] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);

  const [search, setSearch] = useState("");
  const [objet, setObjet] = useState("");
  const [message, setMessage] = useState("");

  const [fetchingUsers, setFetchingUsers] = useState(false);
  const [loading, setLoading] = useState(false);

  const [preview, setPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [backendErrors, setBackendErrors] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      history.push("/login");
      return;
    }
    fetchUtilisateurs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  const fetchUtilisateurs = async () => {
    setFetchingUsers(true);
    setError("");
    setSuccess("");
    setBackendErrors([]);
    try {
      const token = localStorage.getItem("accessToken");
      const res = await axios.get(`${API_BASE}/api/admin/email/utilisateurs`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data?.success) setUtilisateurs(res.data.utilisateurs || []);
      else setError(res.data?.message || "Erreur lors de la récupération des utilisateurs.");
    } catch {
      setError("Erreur de connexion au serveur.");
    } finally {
      setFetchingUsers(false);
    }
  };

  const normalize = (v) => (v ?? "").toString().toLowerCase().trim();

  const filteredUsers = useMemo(() => {
    const q = normalize(search);
    if (!q) return utilisateurs;
    return utilisateurs.filter((u) => {
      const blob = [u?.nom, u?.prenom, u?.email, u?.statut].map(normalize).join(" ");
      return blob.includes(q);
    });
  }, [utilisateurs, search]);

  const selectedSet = useMemo(() => new Set(selectedUsers), [selectedUsers]);

  const canPreview = objet.trim().length > 0 && message.trim().length > 0;
  const canSend = canPreview && (mode === "ALL" || selectedUsers.length > 0);

  const payload = () => ({
    objet,
    message,
    envoyer_a_tous: mode === "ALL",
    destinataires: mode === "SELECT" ? selectedUsers : [],
  });

  const switchToAll = () => {
    setMode("ALL");
    setSelectedUsers([]); // anti-ambiguïté
    setError("");
    setSuccess("");
    setBackendErrors([]);
  };

  const switchToSelect = () => {
    setMode("SELECT");
    setError("");
    setSuccess("");
    setBackendErrors([]);
  };

  const toggleUser = (id) => {
    if (mode !== "SELECT") return;
    setSelectedUsers((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const clearSelection = () => setSelectedUsers([]);

  const selectAllFiltered = () => {
    if (mode !== "SELECT") return;
    const ids = filteredUsers.map((u) => u.id);
    if (!ids.length) return;

    const allSelected = ids.every((id) => selectedSet.has(id));
    if (allSelected) setSelectedUsers((prev) => prev.filter((id) => !ids.includes(id)));
    else setSelectedUsers((prev) => Array.from(new Set([...prev, ...ids])));
  };

  const handlePreview = async () => {
    setError("");
    setSuccess("");
    setBackendErrors([]);

    if (!canPreview) {
      setError("Veuillez remplir l’objet et le message.");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("accessToken");
      const res = await axios.post(`${API_BASE}/api/admin/email/previsualiser`, payload(), {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data?.success) {
        setPreview(res.data.previsualisation);
        setShowPreview(true);
      } else {
        setError(res.data?.message || "Prévisualisation impossible.");
        setBackendErrors(res.data?.erreurs || []);
      }
    } catch (e) {
      const data = e?.response?.data;
      setError(data?.message || "Erreur de connexion au serveur.");
      setBackendErrors(data?.erreurs || []);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    setError("");
    setSuccess("");
    setBackendErrors([]);

    if (!canSend) {
      if (!canPreview) setError("Objet et message requis.");
      else setError("Sélection vide : choisis au moins 1 utilisateur (ou passe en mode Tous).");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("accessToken");
      const res = await axios.post(`${API_BASE}/api/admin/email/envoyer`, payload(), {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data?.success) {
        setSuccess(res.data.message);
        if (Array.isArray(res.data.erreurs) && res.data.erreurs.length) setBackendErrors(res.data.erreurs);

        // reset
        setObjet("");
        setMessage("");
        setSearch("");
        setSelectedUsers([]);
        setMode("SELECT");
        setPreview(null);
        setShowPreview(false);
      } else {
        setError(res.data?.message || "Échec lors de l'envoi.");
        setBackendErrors(res.data?.erreurs || []);
      }
    } catch (e) {
      const data = e?.response?.data;
      setError(data?.message || "Échec lors de l'envoi.");
      setBackendErrors(data?.erreurs || []);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mailPage">
      <style>{css(SIDEBAR_W)}</style>
      <Sidebar />

      <div className="mailMain">
        <div className="container">
          <div className="head">
            <div>
              <h1>Envoi d’emails</h1>
              <p>Choisis <b>Tous</b> ou <b>Sélection</b>. Aucun mélange possible.</p>
            </div>

            <div className="headBtns">
              <button className="btn ghost" onClick={fetchUtilisateurs} disabled={fetchingUsers}>
                {fetchingUsers ? "Rafraîch…" : "Rafraîchir"}
              </button>
              <button className="btn primary" onClick={handlePreview} disabled={loading || !canPreview}>
                {loading ? "…" : "Prévisualiser"}
              </button>
              <button className="btn dark" onClick={handleSend} disabled={loading || !canSend}>
                {loading ? "Envoi…" : "Envoyer"}
              </button>
            </div>
          </div>

          {(error || success) && (
            <div className={`alert ${error ? "danger" : "success"}`}>
              <div className="alertTitle">{error ? "Erreur" : "Succès"}</div>
              <div className="alertText">{error || success}</div>
              {backendErrors?.length > 0 && (
                <ul className="alertList">
                  {backendErrors.map((x, i) => <li key={i}>{x}</li>)}
                </ul>
              )}
            </div>
          )}

          <div className="panel">
            <div className="seg">
              <button className={`segBtn ${mode === "SELECT" ? "on" : ""}`} onClick={switchToSelect} type="button">
                Sélection
              </button>
              <button className={`segBtn ${mode === "ALL" ? "on" : ""}`} onClick={switchToAll} type="button">
                Tous
              </button>
            </div>

            <div className="stats">
              <span>Total <b>{utilisateurs.length}</b></span>
              <span>Filtrés <b>{filteredUsers.length}</b></span>
              <span>Sélection <b>{selectedUsers.length}</b></span>
            </div>
          </div>

          <div className="grid">
            {/* LEFT */}
            <div className="card">
              <div className="cardHead">
                <div>
                  <div className="cardTitle">Rédaction</div>
                  <div className="cardSub">
                    {mode === "ALL"
                      ? <>Envoi à <b>tous</b> les utilisateurs.</>
                      : <>Choisis des destinataires à droite.</>}
                  </div>
                </div>
              </div>

              <div className="cardBody">
                <div className="field">
                  <label>Objet</label>
                  <input value={objet} onChange={(e) => setObjet(e.target.value)} placeholder="Objet…" />
                </div>

                <div className="field">
                  <label>Message</label>
                  <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Ton message…" rows={12} />
                  <div className="mini">
                    <span>Objet: {objet.length}</span>
                    <span>Message: {message.length}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT */}
            <div className="card">
              <div className="cardHead">
                <div>
                  <div className="cardTitle">Destinataires</div>
                  <div className="cardSub">Recherche + coche (désactivé en mode Tous)</div>
                </div>

                <div className="toolsRight">
                  <button className="btn small" onClick={selectAllFiltered} disabled={mode === "ALL" || filteredUsers.length === 0}>
                    Tout (filtré)
                  </button>
                  <button className="btn small" onClick={clearSelection} disabled={mode === "ALL" || selectedUsers.length === 0}>
                    Vider
                  </button>
                </div>
              </div>

              <div className={`tools ${mode === "ALL" ? "disabled" : ""}`}>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={mode === "ALL" ? "Mode Tous activé" : "Rechercher (nom, prénom, email, statut)…"}
                  disabled={mode === "ALL"}
                />
              </div>

              <div className={`list ${mode === "ALL" ? "disabled" : ""}`}>
                {fetchingUsers ? (
                  <div className="empty">Chargement…</div>
                ) : filteredUsers.length === 0 ? (
                  <div className="empty">Aucun résultat.</div>
                ) : (
                  filteredUsers.map((u) => {
                    const checked = selectedSet.has(u.id);
                    return (
                      <div className={`row ${checked ? "on" : ""}`} key={u.id} onClick={() => toggleUser(u.id)}>
                        <input
                          className="cb"
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleUser(u.id)}
                          disabled={mode === "ALL"}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="u">
                          <div className="uTop">
                            <div className="uName">{u?.prenom} {u?.nom}</div>
                            <span className={`status ${statusClass(u?.statut)}`}>{u?.statut || "—"}</span>
                          </div>
                          <div className="uMail">{u?.email || "—"}</div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          {/* MODAL */}
          {showPreview && preview && (
            <div className="overlay" onClick={() => setShowPreview(false)}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <div className="modalHead">
                  <div>
                    <div className="modalTitle">Prévisualisation</div>
                    <div className="modalSub">Aucun texte “bizarre” ajouté : c’est ton message tel quel.</div>
                  </div>
                  <button className="btn ghost" onClick={() => setShowPreview(false)} type="button">Fermer</button>
                </div>

                <div className="modalBody">
                  <div className="kv"><span>Mode</span><b>{preview.mode}</b></div>
                  <div className="kv"><span>Objet</span><b>{preview.objet}</b></div>
                  <div className="kv"><span>Destinataires</span><b>{preview.destinataires}</b></div>
                  <div className="kv"><span>Nombre</span><b>{preview.nombre_destinataires}</b></div>

                  <div className="msgBox">
                    <div className="msgTitle">Message</div>
                    <pre>{preview.message}</pre>
                  </div>
                </div>

                <div className="modalFoot">
                  <button className="btn dark" onClick={handleSend} disabled={loading || !canSend} type="button">
                    {loading ? "Envoi…" : "Envoyer"}
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

function statusClass(statut) {
  const s = (statut || "").toString().toLowerCase();
  if (s.includes("valid") || s.includes("accept")) return "ok";
  if (s.includes("rejet") || s.includes("refus")) return "no";
  return "wait";
}

function css(sidebarW) {
  return `
  *{ box-sizing:border-box; }
  .mailPage{ min-height:100vh; background:#f3f6fb; }
  .mailMain{
    padding: 22px 18px 60px;
    margin-left: ${sidebarW}px; /* ✅ espace sidebar */
  }
  @media (max-width: 991.98px){
    .mailMain{ margin-left:0; padding: 16px 12px 40px; }
  }

  /* ✅ IMPORTANT: on aligne à gauche (pas de center qui pousse à droite) */
  .container{
    max-width: 1200px;
    margin-left: 0;
    margin-right: auto;
  }
  @media (max-width: 1300px){
    .container{ margin-left:auto; margin-right:auto; }
  }

  .head{
    display:flex; align-items:flex-end; justify-content:space-between;
    gap:14px; padding: 4px 2px 16px;
  }
  .head h1{ margin:0; font-size:28px; font-weight:900; color:#0f172a; letter-spacing:-.3px; }
  .head p{ margin:6px 0 0; color:#475569; font-size:13px; }
  .headBtns{ display:flex; gap:10px; flex-wrap:wrap; }

  .alert{
    background:#fff; border:1px solid rgba(2,6,23,.10);
    border-radius:16px; padding: 12px 14px; margin: 0 0 14px;
    box-shadow: 0 12px 28px rgba(2,6,23,.06);
  }
  .alert.danger{ background: rgba(225,29,72,.06); border-color: rgba(225,29,72,.22); }
  .alert.success{ background: rgba(34,197,94,.06); border-color: rgba(34,197,94,.22); }
  .alertTitle{ font-weight:900; margin-bottom:4px; }
  .alertText{ font-weight:800; }
  .alertList{ margin:10px 0 0; padding-left:18px; font-size:12px; color:#0f172a; }

  .panel{
    background:#fff; border:1px solid rgba(2,6,23,.10);
    border-radius:18px; padding:12px; margin-bottom:14px;
    display:flex; justify-content:space-between; align-items:center; gap:12px;
  }
  .seg{
    display:flex; gap:8px; background: rgba(2,6,23,.04); border:1px solid rgba(2,6,23,.08);
    padding:6px; border-radius:14px;
  }
  .segBtn{
    border:none; background:transparent; cursor:pointer;
    padding:10px 14px; border-radius:12px; font-weight:900;
  }
  .segBtn.on{ background:#fff; box-shadow: 0 10px 24px rgba(2,6,23,.08); }
  .stats{ display:flex; gap:10px; flex-wrap:wrap; color:#334155; font-weight:700; font-size:12px; }
  .stats b{ color:#0f172a; }

  .grid{
    display:grid; grid-template-columns: 1fr 1fr; gap:14px; align-items:start;
  }
  @media (max-width: 1100px){
    .grid{ grid-template-columns: 1fr; }
  }

  .card{
    background:#fff; border:1px solid rgba(2,6,23,.10);
    border-radius:18px; overflow:hidden;
    box-shadow: 0 18px 40px rgba(2,6,23,.06);
  }
  .cardHead{
    padding:14px 16px; border-bottom:1px solid rgba(2,6,23,.08);
    display:flex; justify-content:space-between; align-items:flex-start; gap:12px;
  }
  .cardTitle{ font-weight:900; font-size:15px; }
  .cardSub{ font-size:12px; color:#64748b; margin-top:3px; }
  .cardBody{ padding:14px 16px 16px; }

  .toolsRight{ display:flex; gap:8px; }
  .tools{ padding:12px 16px; border-bottom:1px solid rgba(2,6,23,.08); }
  .tools.disabled{ opacity:.6; pointer-events:none; }

  label{ font-size:12px; font-weight:900; color:#334155; }
  .field{ display:flex; flex-direction:column; gap:8px; margin-bottom:12px; }

  input, textarea{
    width:100%; border-radius:14px;
    border:1px solid rgba(15,23,42,.12);
    background:#f8fafc; padding:12px;
    outline:none;
  }
  input:focus, textarea:focus{
    background:#fff;
    border-color: rgba(37,99,235,.35);
    box-shadow: 0 0 0 4px rgba(37,99,235,.12);
  }
  .mini{ display:flex; justify-content:space-between; font-size:12px; color:#64748b; font-weight:700; }

  .list{ max-height: 560px; overflow:auto; padding:12px; }
  .list.disabled{ opacity:.6; pointer-events:none; }

  .row{
    display:flex; align-items:center; gap:12px;
    padding:12px; border:1px solid rgba(2,6,23,.08);
    border-radius:16px; margin-bottom:10px;
    cursor:pointer;
    transition: transform .12s ease, box-shadow .12s ease, border-color .12s ease;
  }
  .row:hover{ transform: translateY(-1px); box-shadow: 0 12px 26px rgba(2,6,23,.06); border-color: rgba(37,99,235,.22); }
  .row.on{ background: rgba(37,99,235,.06); border-color: rgba(37,99,235,.30); }

  .cb{ width:20px; height:20px; }

  .u{ flex:1; min-width:0; }
  .uTop{ display:flex; justify-content:space-between; align-items:center; gap:10px; }
  .uName{ font-weight:900; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .uMail{ margin-top:4px; font-size:12px; color:#64748b; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }

  .status{
    font-size:11px; font-weight:900; padding:6px 10px; border-radius:999px;
    border:1px solid rgba(2,6,23,.10); background: rgba(2,6,23,.04); color:#334155;
  }
  .status.ok{ background: rgba(34,197,94,.10); border-color: rgba(34,197,94,.20); color:#166534; }
  .status.no{ background: rgba(225,29,72,.10); border-color: rgba(225,29,72,.20); color:#be123c; }
  .status.wait{ background: rgba(234,179,8,.12); border-color: rgba(234,179,8,.22); color:#854d0e; }

  .empty{ padding:18px 10px; text-align:center; color:#64748b; font-weight:800; }

  .btn{
    border-radius:14px;
    border:1px solid rgba(15,23,42,.14);
    background:#fff; color:#0f172a;
    padding:11px 14px;
    font-weight:900; cursor:pointer;
    transition: transform .12s ease, box-shadow .12s ease;
  }
  .btn:hover{ transform: translateY(-1px); box-shadow: 0 12px 24px rgba(2,6,23,.10); }
  .btn:disabled{ opacity:.6; cursor:not-allowed; transform:none; box-shadow:none; }
  .btn.ghost{ background: rgba(2,6,23,.04); }
  .btn.primary{ background: rgba(37,99,235,.10); border-color: rgba(37,99,235,.20); color:#1d4ed8; }
  .btn.dark{ background:#0f172a; border-color:transparent; color:#fff; }
  .btn.small{ padding:10px 12px; font-size:12px; }

  .overlay{
    position:fixed; inset:0; background: rgba(2,6,23,.55);
    display:flex; align-items:center; justify-content:center;
    padding:16px; z-index: 9000;
  }
  .modal{
    width: min(860px, 100%);
    background:#fff; border-radius:18px; overflow:hidden;
    box-shadow: 0 30px 90px rgba(0,0,0,.35);
  }
  .modalHead{
    padding:14px 16px; border-bottom:1px solid rgba(2,6,23,.08);
    display:flex; justify-content:space-between; align-items:flex-start; gap:12px;
  }
  .modalTitle{ font-weight:900; }
  .modalSub{ margin-top:3px; font-size:12px; color:#64748b; }
  .modalBody{ padding:14px 16px; }
  .kv{ display:flex; justify-content:space-between; gap:10px; padding:10px 0; border-bottom:1px dashed rgba(2,6,23,.12); }
  .kv span{ font-size:12px; font-weight:900; color:#64748b; }
  .msgBox{ margin-top:12px; background:#f8fafc; border:1px solid rgba(2,6,23,.10); border-radius:14px; padding:12px; }
  .msgTitle{ font-weight:900; margin-bottom:8px; }
  .msgBox pre{ margin:0; white-space:pre-wrap; line-height:1.5; font-weight:600; font-size:13px; color:#0f172a; }
  .modalFoot{ padding:14px 16px; border-top:1px solid rgba(2,6,23,.08); display:flex; justify-content:flex-end; }
  `;
}
